import { Flag, Trophy } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  missionRewardGames,
  missionRewardPrize,
} from "@/campaigns/modules/missions/types";
import type { SprintMission } from "@/campaigns/modules/sprint-mission/types";
import {
  completedTaskCount,
  hasWon,
} from "@/campaigns/modules/sprint-mission/types";
import { PrizeGamesChips } from "@/components/campaigns/prize-games-chips";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { formatPrizeTitle } from "@/utils/helpers";

import { useSprintMissionPhase } from "./use-sprint-mission-phase";
import { PrizeText } from "@/components/prize-text";

/**
 * The status card that tops the main column once there is something to say about *this
 * player's* outcome: they won a slot, or the run is over.
 *
 * - `won` — a green-stroked card: "You are a Winner!" with the prize in gold and, for free
 *   spins, the games line under it. The winners card
 *   underneath stays live, because the run is still going for everyone else. When the pool pays
 *   out at campaign end, `SprintMissionPayoutStrip` (its own component) sits directly under it.
 * - `finished` — "Finished / No more winners / You completed N of M tasks". Replaces the generic
 *   `CampaignFinishedCard` for this type: that card prints `campaign.end`, which is wrong for a
 *   pool that ran dry before its date. A winner of a finished run is told so on the third line.
 *
 * Renders nothing in the live phases.
 */
export const SprintMissionStatusCard = ({
  sprintMission,
  className,
}: {
  sprintMission: SprintMission;
  className?: string;
}) => {
  const { t } = useTranslation();
  const phase = useSprintMissionPhase(sprintMission);
  const mission = sprintMission.mission;
  const won = hasWon(sprintMission);
  const prize = mission ? missionRewardPrize(mission) : null;
  const prizeTitle = prize ? formatPrizeTitle(prize.title, t) : null;

  if (phase === "won") {
    return (
      <Card
        inset="none"
        active
        data-phase={phase}
        className={cn(
          "flex items-center justify-between gap-4 px-4 py-3",
          className
        )}
      >
        <span className="flex shrink-0 items-center gap-2">
          <Trophy className="size-5 shrink-0 text-active" strokeWidth={2} />
          <h3 className="font-heading text-base leading-5 font-bold text-active">
            {t("sprintMission.youAreAWinner", "You are a Winner!")}
          </h3>
        </span>
        {prizeTitle && (
          <span className="flex min-w-0 flex-1 flex-col items-end gap-1.5">
            {/* Never truncated: the prize is the card's priority content (CLAUDE.md) — it
                shrinks, then glides on its one row. */}
            <PrizeText
              size="title"
              icon
              fit="marquee"
              className="max-w-full min-w-0"
            >
              {prizeTitle}
            </PrizeText>
            {/* Free spins: where they land — the same games line as every prize, under it. */}
            {mission && (
              <PrizeGamesChips
                games={missionRewardGames(mission)}
                className="max-w-full justify-end"
              />
            )}
          </span>
        )}
      </Card>
    );
  }

  if (phase === "finished") {
    const done = completedTaskCount(sprintMission);
    const total = mission?.tasks.length ?? 0;
    return (
      <Card
        inset="none"
        data-phase={phase}
        className={cn("flex items-center gap-3.5 px-4 py-3.5", className)}
      >
        <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-background-card">
          <Flag className="size-4 text-muted-foreground" strokeWidth={1.5} />
        </span>
        <span className="flex min-w-0 flex-col gap-0.5">
          <h3 className="font-heading text-[13px] leading-[18px] font-bold text-foreground lg:text-[15px] lg:leading-5">
            {t("sprintMission.finishedShort", "Finished")}
          </h3>
          <p className="font-body text-[12px] leading-4 text-foreground-secondary lg:text-[13px] lg:leading-[18px]">
            {t("sprintMission.noMoreWinners", "No more winners")}
          </p>
          <p className="font-body text-[12px] leading-4 text-muted-foreground lg:text-[13px] lg:leading-[18px]">
            {won && prizeTitle
              ? t("sprintMission.youWonPrize", "You won {{prize}}", {
                  prize: prizeTitle,
                })
              : total > 0
                ? t(
                    "sprintMission.youCompletedTasks",
                    "You completed {{done}} of {{total}} tasks",
                    { done, total }
                  )
                : null}
          </p>
        </span>
      </Card>
    );
  }

  return null;
};
